import { useContext, useEffect, JSX, useState } from 'react';
import { sources } from '../api/sources';
import {
  AppHeader,
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ace-clublabs/shared-component-library';
import { BillPlan, INewMembershipInfo, IPayPlanCosts, Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { benefitsHelper } from '../utils/benefitsHelper';
import { DisclaimerDisplay } from '../components/DisclaimerDisplay';
import CookieHelper from '../utils/cookieHelper';
import { UtagType } from '../utils/UtagHelper';
import { getDefaultMembershipLevel, stripNonWhitelistedQueryParams } from '../utils/helpers';
import { CurrentPayPlan, FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { useHistory } from 'react-router-dom';
import { getNextPage } from '../utils/getNextPage';
import { SELECTED_MEMBERSHIP_LEVEL_URL_QUERY_PARAM, SELECTED_MEMBERSHIP_TERM_URL_QUERY_PARAM } from '../shared/types';
import { ProductType } from '../application/models/NewJoinSessionData';
import { GreenBoxMessage } from '../components/BenefitPageComponents/GreenBoxMessage';
import { MembershipTermSelector } from '../components/BenefitPageComponents/MembershipTermSelector';
import { MembershipInfo } from '../components/BenefitPageComponents/MembershipInfo';
import { ContactSupportCard } from '../components/ContactSupportCard';
import LogCannon from '@ace-clublabs/log-cannon';

export type MembershipTerm = 'annual' | 'monthly';

interface BenefitsProps {
  isUpgrading?: boolean;
  onContinue?: () => void;
}

export const BenefitsPage: (props: BenefitsProps) => JSX.Element = ({ isUpgrading, onContinue }) => {
  const {
    loadingInfo,
    sessionData,
    membershipInfo,
    refreshSession,
    sendTag,
    updateMembershipForm,
    getNewBusinessCost,
  } = useContext(MembershipAppStateContext);
  const history = useHistory();
  const [isTagging, setIsTagging] = useState(true);
  const currentUrl = window.location.href;
  const urlSearchParams = new URL(currentUrl).searchParams;
  const urlSelectedLevel = urlSearchParams.get(SELECTED_MEMBERSHIP_LEVEL_URL_QUERY_PARAM) as MembershipLevel;
  const urlSelectedTerm = urlSearchParams.get(SELECTED_MEMBERSHIP_TERM_URL_QUERY_PARAM) as MembershipTerm;
  const isLoading = ((loadingInfo.isLoading || isTagging) && !isUpgrading) || membershipInfo?.benefits == null;

  const [selectedMembershipLevel, setSelectedMembershipLevel] = useState<MembershipLevel>(
    urlSelectedLevel ??
      sessionData.formData.membershipLevel ??
      getDefaultMembershipLevel(membershipInfo?.allowedMembershipLevels)
  );

  const [selectedTerm, setSelectedTerm] = useState(
    (): MembershipTerm =>
      urlSelectedTerm ?? (sessionData.formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY ? 'monthly' : 'annual')
  );

  const isMonthlySelected = selectedTerm === 'monthly';
  const membershipCosts = getMembershipCosts(membershipInfo!, selectedMembershipLevel, selectedTerm);
  const admissionFee = membershipCosts?.admissionFee?.baseCost ?? 0;
  const admissionDiscount = membershipCosts?.admissionFee?.discountAmount ?? 0;

  const shortBenefits = benefitsHelper.getBenefitsByMembershipLevel(
    sessionData,
    selectedMembershipLevel
  )?.shortBenefits;

  useEffect(() => {
    if (urlSelectedLevel === selectedMembershipLevel) {
      urlSearchParams.delete(SELECTED_MEMBERSHIP_LEVEL_URL_QUERY_PARAM);
      urlSearchParams.delete(SELECTED_MEMBERSHIP_TERM_URL_QUERY_PARAM);
      const params = urlSearchParams.toString();
      const newUrl = currentUrl.split('?')[0] + `${params ? `?${params}` : ''}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [selectedMembershipLevel]);

  const addZipRouter = () => {
    const zipRouterScript = document.createElement('script');
    zipRouterScript.src = 'https://app.ace.aaa.com/ziprouter/1.0.9/ace-zip-router.js';
    zipRouterScript.type = 'text/javascript';
    zipRouterScript.id = 'zip-router-modal-script';
    document.body.appendChild(zipRouterScript);
  };

  const tag = () => {
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:select level' : 'select level';
    sendTag(UtagType.view, tagPage);
    setIsTagging(false);
  };

  useEffect(() => {
    refreshSession();
    const handleZipRouteUpdate = () => {
      tag();
    };
    document.addEventListener('zipRouteUpdate', handleZipRouteUpdate);
    return () => {
      document.removeEventListener('zipRouteUpdate', handleZipRouteUpdate);
    };
  }, []);

  useEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }

    // zip router
    const currentUrl = window.location.href;
    const aaaHostname = window.location.hostname.includes('aaa.com');
    const urlSearchParams = new URL(currentUrl).searchParams;
    const zipCode = sessionData.zipCode;

    const zipCodeFromCookie = CookieHelper.getZipcodeFromZipcodeCookie();

    if (!aaaHostname || zipCodeFromCookie) {
      tag();
      return;
    }

    if (zipCode) {
      urlSearchParams.set('zip', zipCode);
      const newUrl = currentUrl.split('?')[0] + '?' + urlSearchParams.toString();
      window.history.pushState({ path: newUrl }, '', newUrl);
    }

    addZipRouter();
  }, [membershipInfo]);

  const handleContinueClick = (memLevel?: MembershipLevel) => {
    const defaultAnnualPlan =
      membershipInfo?.allowedBillPlans?.find((bp) => bp === CurrentPayPlan.RECURRINGCREDITORDEBITCARD) ??
      membershipInfo?.allowedBillPlans?.find((bp) => bp === CurrentPayPlan.ONETIME);

    const currentBillPlanIsAnnual = sessionData?.formData?.billPlan !== CurrentPayPlan.RECURRINGMONTHLY;

    let { previousAnnualBillPlan, firstSelectedMembershipLevel } = sessionData.formData;
    if (currentBillPlanIsAnnual) {
      previousAnnualBillPlan = sessionData.formData.billPlan;
    } else if (previousAnnualBillPlan == undefined) {
      previousAnnualBillPlan = defaultAnnualPlan as CurrentPayPlan;
    }

    const billPlan = isMonthlySelected ? CurrentPayPlan.RECURRINGMONTHLY : previousAnnualBillPlan;

    const updatedFormData = {
      ...sessionData.formData,
      membershipLevel: memLevel ?? selectedMembershipLevel,
      billPlan,
      previousAnnualBillPlan,
      firstSelectedMembershipLevel: firstSelectedMembershipLevel ?? memLevel ?? selectedMembershipLevel, // for tagging
    };

    const membershipLevelAllowsRV = membershipInfo?.membershipProducts.RV.includes(memLevel ?? selectedMembershipLevel);
    const hasRVSelected = updatedFormData.products && updatedFormData.products.includes(ProductType.RV);

    if (!membershipLevelAllowsRV && hasRVSelected) {
      const removedRVProducts = updatedFormData.products?.filter((product) => product !== ProductType.RV);
      updatedFormData.products = removedRVProducts?.length === 0 ? undefined : removedRVProducts;
    }

    updateMembershipForm(updatedFormData);

    if (isUpgrading && onContinue) {
      getNewBusinessCost(updatedFormData).then(() => {
        sources
          .processRexEvent({
            action: 'post',
            sessionId: sessionData?.sessionId || '',
            flowId: sessionData?.flowType || '',
            screenId: 'benefits',
            eventType: 'INPROGRESS',
            eventData: updatedFormData,
          })
          .catch(() => {
            LogCannon.error('Failed to process rex event', {
              sessionId: sessionData?.sessionId || '',
            });
          });
      });
      onContinue();
    } else {
      const currentUrl = window.location.href;
      const urlSearchParams = new URL(currentUrl).searchParams;
      const params = urlSearchParams.toString();
      const urlWithWhiteListedParams = stripNonWhitelistedQueryParams(currentUrl, params);
      window.history.replaceState(null, '', urlWithWhiteListedParams);
      history.push(getNextPage(Pages.BenefitsPage, sessionData.flowType));
    }
  };

  const togglePayTerm = () => {
    setSelectedTerm((currentTerm) => (currentTerm === 'annual' ? 'monthly' : 'annual'));
  };

  const applyAutopayDiscount = (membershipCosts: IPayPlanCosts): boolean => {
    const primaryMemberBaseCost = membershipCosts.primary?.baseCost;
    const primaryMemberDiscountAmount = membershipCosts.primary?.discountAmount;
    const autoPayDiscount = membershipCosts.autoPay?.discountAmount ?? 0;
    const primaryMemberCostTotal = primaryMemberBaseCost - primaryMemberDiscountAmount;
    const { billPlan, previousAnnualBillPlan } = sessionData.formData;
    const AC = CurrentPayPlan.RECURRINGCREDITORDEBITCARD;
    const isAnnualBillPlanAC = billPlan === AC || previousAnnualBillPlan === AC;
    const AMNotAllowed = !membershipInfo?.allowedBillPlans.includes(BillPlan.AM);
    return (isAnnualBillPlanAC || AMNotAllowed) && primaryMemberCostTotal > autoPayDiscount;
  };

  const hasDiscountApplied = (membershipCosts: IPayPlanCosts): boolean => {
    if (sessionData.flowType === FlowType.MAJOR_ROCKET || sessionData.flowType === FlowType.GROUPON) {
      return false;
    }
    const primaryMemberDiscountAmount = membershipCosts.primary?.discountAmount;
    const autoPayDiscount = membershipCosts.autoPay?.discountAmount ?? 0;
    return primaryMemberDiscountAmount > 0 || (applyAutopayDiscount(membershipCosts) && autoPayDiscount > 0);
  };

  const handleButtonClick = (memLevel: MembershipLevel) => {
    setSelectedMembershipLevel(memLevel);
    sendTag(UtagType.dispatch, 'select level');
  };

  const handleSelectAndContinue = () => {
    return (memLevel: MembershipLevel) => {
      setSelectedMembershipLevel(memLevel);
      sendTag(UtagType.dispatch, 'select level');
      handleContinueClick(memLevel);
    };
  };

  const membershipPayPlanCosts: IPayPlanCosts | null | undefined = getMembershipCosts(
    membershipInfo!,
    selectedMembershipLevel,
    selectedTerm
  );

  let discountApplied: boolean = false;
  if (membershipPayPlanCosts != null && membershipPayPlanCosts != undefined) {
    discountApplied = hasDiscountApplied(membershipPayPlanCosts);
  }

  const theme = useTheme();
  const isMobileScreenSize = useMediaQuery(theme.breakpoints.down('lg'));

  if (isLoading) {
    return (
      <>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </>
    );
  }

  return (
    <>
      <Stack className="benefit-page-view">
        <AppHeader sx={{ padding: 0 }} />
        <Typography variant="h5Medium" marginBottom={'1.5rem'} className="bill-plan-title" alignSelf={'center'}>
          Start driving with peace of mind
        </Typography>
        <Stack className="benefit-content-align">
          <MembershipTermSelector
            membershipInfo={membershipInfo}
            togglePayTerm={togglePayTerm}
            selectedTerm={selectedTerm}
            sendTag={sendTag}
            hasGreenBoxMessage={!!sessionData.membershipContent?.greenBoxMessage?.shortBoxMessage}
          />
          <GreenBoxMessage
            flowType={sessionData.flowType!}
            shortBoxMessage={sessionData.membershipContent?.greenBoxMessage?.shortBoxMessage}
            modalTitle={sessionData.membershipContent?.greenBoxMessage?.modalTitle ?? undefined}
            modalBody={sessionData.membershipContent?.greenBoxMessage?.modalBody ?? undefined}
          />
          <Box className="section-with-background" width={'100%'} sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#FAF8F4',
                zIndex: -1,
                width: '100vw',
              }}
            />

            <MembershipInfo
              membershipInfo={membershipInfo}
              selectedMembershipLevel={selectedMembershipLevel}
              selectedTerm={selectedTerm}
              sessionData={sessionData}
              handleButtonClick={handleButtonClick}
              handleContinueClick={handleContinueClick}
              handleSelectAndContinue={handleSelectAndContinue}
              isUpgrading={isUpgrading}
              admissionDiscount={admissionDiscount}
              admissionFee={admissionFee}
            />
          </Box>
        </Stack>
        {isMobileScreenSize && <ContactSupportCard styleOverrides={{ marginTop: '1rem' }} />}
        <div className="benefits-page-disclaimer-section">
          <DisclaimerDisplay
            disclaimers={sessionData.membershipContent?.disclaimerMessage}
            payTerm={selectedTerm}
            allowedBillPlans={membershipInfo.allowedBillPlans as CurrentPayPlan[]}
            {...(!isMobileScreenSize
              ? { styleOverrides: { containerStyle: { paddingBottom: '', paddingTop: '1.75rem' } } }
              : {})}
          />
        </div>
        {!isMobileScreenSize && (
          <Grid container>
            <Grid size={7}>
              <ContactSupportCard />
            </Grid>{' '}
          </Grid>
        )}
      </Stack>
    </>
  );
};

export const getMembershipCosts = (
  membershipInfo: INewMembershipInfo,
  selectedMembershipLevel: MembershipLevel,
  selectedTerm: MembershipTerm
): IPayPlanCosts | null | undefined => {
  let membershipCost: any;

  switch (selectedMembershipLevel) {
    case MembershipLevel.CLASSIC:
      membershipCost = membershipInfo?.membershipCost?.CLASSIC;
      break;
    case MembershipLevel.PLUS:
      membershipCost = membershipInfo?.membershipCost?.PLUS;
      break;
    case MembershipLevel.PREMIER:
      membershipCost = membershipInfo?.membershipCost?.PREMIER;
      break;
    default:
      return null;
  }
  // For major rocket return only the base cost without discount applied (so that the display cost will show but with strikethrough)
  const membershipCosts = membershipCost?.[selectedTerm];
  return membershipCosts;
};
